import { Button, Container, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, makeStyles, TextField } from '@material-ui/core';
import React, { useState } from 'react';
import useUserSearch from '../../../../../../hooks/useUserSearch';
import { useDebounce } from '../../../../../../hooks/useDebounce';
import { Alert, Autocomplete } from '@material-ui/lab';
import usePlanSearch from '../../../../../../hooks/usePlanSearch';
import { producaov2 } from '../../../../../../services/apis';
import { addDays, differenceInDays, format } from 'date-fns';
import ModalCreatePlan from '../../../../../../components/Admin/Pagarme/ModalCreatePlan';

const useStyles = makeStyles(() => ({
  container: {
    padding: '1rem'
  }
}))

/**
 * 
 * @param {import('@material-ui/core').TextFieldProps} props 
 * @returns 
 */
const DefaultTextField = (props) => {
  return (
    <TextField
      key={props?.defaultValue}
      fullWidth
      variant="outlined"
      {...props}
      helperText={props?.errorMessage ? props.errorMessage : props?.helperText}
      error={!!props?.errorMessage}
    />
  )
}

/**
 * 
 * @param {import('@material-ui/lab').AutocompleteProps} props
 * @returns 
 */
const DefaultAutoComplete = (props) => {
  return (
    <Autocomplete
      fullWidth
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={props?.label}
          helperText={props?.errorMessage ? props.errorMessage : props?.helperText}
          error={!!props?.errorMessage}
        />
      )}
      {...props}
    />
  )
}

const FORM_FIELDS = {
  USER_ID: 'userID',
  PLAN_ID: 'planID',
  DUE_AT: 'due_at',
  CPF: 'cpf',
  PHONE: 'celular',
  CEP: 'cep',
  STREET: 'street',
  NUMBER: 'number',
  DISTRICT: 'district',
  COMPLEMENT: 'complement',
  STATE: 'state',
  CITY: 'city'
}

const validateForm = (formValues) => {
  const optionalFields = [FORM_FIELDS.COMPLEMENT, FORM_FIELDS.DUE_AT];
  
  const formErrors = Object.entries(formValues).reduce((prev, [key, value]) => {
    if(!value && !optionalFields.includes(key)) {
      return {
        ...prev,
        [key]: 'Campo obrigatório'
      }
    }

    return prev;
  }, {});

  return {
    hasError: Object.values(formErrors).length > 0,
    errors: formErrors
  };
}

export default function BoletoSubscription() {
  const classnames = useStyles();

  const [userSearch, setUserSearch] = useState('');
  const debouncedUserSearch = useDebounce(userSearch);
  const { data: users, loading: loadingUsers } = useUserSearch({ search: debouncedUserSearch });
  const [selectedUser, setSelectedUser] = useState();

  const [planSearch, setPlanSearch] = useState('');
  const debouncedPlanSearch = useDebounce(planSearch);
  const { data: plans, loading: loadingPlans } = usePlanSearch({ search: debouncedPlanSearch, payment_method: 'boleto' });
  const [selectedPlan, setSelectedPlan] = useState();

  const [loading, setLoading] = useState(false);
  const debouncedLoading = useDebounce(loading, 100);

  const [errors, setErrors] = useState({});
  const [generatedBoleto, setGeneratedBoleto] = useState();
  const [createPlanModalOpen, setCreatePlanModalOpen] = useState(false);
  const [dueAt, setDueAt] = useState('');

  /**
   * Prazo maximo, em dias, que cabe dentro de um ciclo mensal. Acima disso a
   * assinatura passa a comecar mais tarde (o backend usa start_at) e nenhum
   * boleto e gerado agora.
   */
  const MAX_DIAS_DENTRO_DO_CICLO = 27;

  const diasAteVencimento = dueAt
    ? differenceInDays(new Date(`${dueAt}T23:59:59`), new Date())
    : 0;

  const vencimentoAdiaInicio = diasAteVencimento > MAX_DIAS_DENTRO_DO_CICLO;

  const clearError = (inputName) => {
    setErrors(prev => ({...prev, [inputName]: ''}));
  }

  const clearErrors = () => {
    setErrors({});
  }

  const toggleCreatePlanModalOpen = () => {
    setCreatePlanModalOpen(prev => !prev);
  }

  /**
   * 
   * @param {React.FormEvent<HTMLFormElement>} e 
   */
  const handleSubmit = async (e) => {
    try {
      e.preventDefault();

      const formData = new FormData(e.target);
      const formValues = Object.fromEntries(formData);

      const formErrors = validateForm({
        ...formValues,
        [FORM_FIELDS.USER_ID]: selectedUser?.id,
        [FORM_FIELDS.PLAN_ID]: selectedPlan?.id_plano
      });

      if(formErrors.hasError) {
        setErrors(formErrors.errors);
        return;
      }

      clearErrors();

      setLoading(true);

      await producaov2.put(`/admin/eadalunos/${selectedUser.id}`, {
        aluno: {
          cpf: formValues[FORM_FIELDS.CPF],
          cep: formValues[FORM_FIELDS.CEP],
          endereco: formValues[FORM_FIELDS.STREET],
          numero: formValues[FORM_FIELDS.NUMBER],
          bairro: formValues[FORM_FIELDS.DISTRICT],
          complemento: formValues[FORM_FIELDS.COMPLEMENT] || '',
          celular: formValues[FORM_FIELDS.PHONE],
          estado: formValues[FORM_FIELDS.STATE],
          cidade: formValues[FORM_FIELDS.CITY],
        }
      });

      const response = await producaov2.post('/admin/orders/subscriptions', {
        userID: selectedUser.id,
        planID: selectedPlan.id_plano,
        dueAt: formValues[FORM_FIELDS.DUE_AT],
        payment: {
          id: 'boleto'
        }
      });

      if(response.data?.status === 'paid') {
        setGeneratedBoleto(response.data?.boleto);
        return;
      }

      swal('Houve um erro ao criar a assinatura', response.data?.message, 'error');
    } catch(error) {
      swal('Houve um erro ao criar a assinatura', error?.response?.data?.message ? error?.response?.data?.message : error?.message, 'error');
    } finally {
      setLoading(false);
    }
  }

  const handleClearForm = () => {
    setSelectedPlan(undefined);
    setSelectedUser(undefined);
    setPlanSearch('');
    setUserSearch('')
    setDueAt('')
  }

  return (
    <Container className={classnames.container} maxWidth="lg" component="form" onSubmit={handleSubmit}>
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <DefaultAutoComplete
            label="Aluno"
            options={users}
            getOptionLabel={(option) => `[${option?.id}] ${option?.nome} - ${option?.email}`}
            inputValue={userSearch}
            onInputChange={(_, newInputValue) => setUserSearch(newInputValue)}
            loading={loadingUsers}
            value={selectedUser}
            onChange={(_, newUser) => setSelectedUser(newUser)}
            errorMessage={errors[FORM_FIELDS.USER_ID]}
            onFocus={() => clearError(FORM_FIELDS.USER_ID)}
          />
        </Grid>
        <Grid item container spacing={2}>
          <Grid item xs>
            <DefaultAutoComplete
              label="Plano"
              options={plans}
              getOptionLabel={(option) => `[${option?.id_plano}] ${option?.nome} (${option?.ciclos_cobranca} x R$${option?.valor})`}
              inputValue={planSearch}
              onInputChange={(_, newInputValue) => setPlanSearch(newInputValue)}
              loading={loadingPlans}
              value={selectedPlan}
              onChange={(_, newPlan) => setSelectedPlan(newPlan)}
              helperText="Planos que possuem boleto como método de pagamento"
              errorMessage={errors[FORM_FIELDS.PLAN_ID]}
              onFocus={() => clearError(FORM_FIELDS.PLAN_ID)}
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              type="button"
              onClick={toggleCreatePlanModalOpen}
            >
              Novo Plano
            </Button>
          </Grid>
        </Grid>
        <Grid item>
          <DefaultTextField
            label="Vencimento do 1º boleto (Opcional)"
            placeholder="dd/mm/yyyy"
            type="date"
            name={FORM_FIELDS.DUE_AT}
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
            InputLabelProps={{
              shrink: true
            }}
            inputProps={{
              min: format(addDays(new Date(), 1), 'yyyy-MM-dd')
            }}
            helperText="Se deixar em branco, o 1º boleto vence em 3 dias. Os boletos seguintes vencem sempre no mesmo dia do mês do primeiro."
          />
        </Grid>
        {vencimentoAdiaInicio && (
          <Grid item>
            <Alert severity="info">
              Como esse vencimento está a {diasAteVencimento} dias de hoje (mais
              de um mês), a assinatura vai <strong>começar</strong> em{' '}
              {format(addDays(new Date(`${dueAt}T23:59:59`), -3), 'dd/MM/yyyy')}{' '}
              em vez de hoje. O aluno já libera o acesso agora, mas o primeiro
              boleto só é gerado nessa data e chega por e-mail — não vai
              aparecer nenhum boleto aqui na tela para você enviar.
              <br />
              <br />
              É assim de propósito: a mensalidade precisa ser cobrada uma vez
              por mês. Se o primeiro boleto vencesse daqui a mais de um mês sem
              adiar o início, o boleto do 2º mês chegaria antes do 1º vencer, o
              aluno pagaria o mais novo achando que era o do mês, e o antigo
              ficaria em aberto derrubando o acesso dele.
            </Alert>
          </Grid>
        )}
        <Grid item>
          <DefaultTextField
            label="CPF ou CNPJ"
            placeholder="CPF ou CNPJ"
            defaultValue={selectedUser?.cpf}
            name={FORM_FIELDS.CPF}
            errorMessage={errors[FORM_FIELDS.CPF]}
            onFocus={() => clearError(FORM_FIELDS.CPF)}
          />
        </Grid>
        <Grid item>
          <DefaultTextField
            label="Celular com DDD"
            placeholder="(31) 99999-9999"
            defaultValue={selectedUser?.celular}
            name={FORM_FIELDS.PHONE}
            errorMessage={errors[FORM_FIELDS.PHONE]}
            onFocus={() => clearError(FORM_FIELDS.PHONE)}
          />
        </Grid>
        <Grid item>
          <DefaultTextField
            label="CEP"
            placeholder="99999-999"
            defaultValue={selectedUser?.cep}
            name={FORM_FIELDS.CEP}
            errorMessage={errors[FORM_FIELDS.CEP]}
            onFocus={() => clearError(FORM_FIELDS.CEP)}
          />
        </Grid>
        <Grid item container spacing={2}>
          <Grid item xs={6}>
            <DefaultTextField
              label="Rua"
              defaultValue={selectedUser?.endereco}
              name={FORM_FIELDS.STREET}
              errorMessage={errors[FORM_FIELDS.STREET]}
              onFocus={() => clearError(FORM_FIELDS.STREET)}
            />
          </Grid>
          <Grid item xs={6}>
            <DefaultTextField
              label="Número"
              defaultValue={selectedUser?.numero}
              name={FORM_FIELDS.NUMBER}
              errorMessage={errors[FORM_FIELDS.NUMBER]}
              onFocus={() => clearError(FORM_FIELDS.NUMBER)}
            />
          </Grid>
        </Grid>
        <Grid item container spacing={2}>
          <Grid item xs={6}>
            <DefaultTextField
              label="Bairro"
              defaultValue={selectedUser?.bairro}
              name={FORM_FIELDS.DISTRICT}
              errorMessage={errors[FORM_FIELDS.DISTRICT]}
              onFocus={() => clearError(FORM_FIELDS.DISTRICT)}
            />
          </Grid>
          <Grid item xs={6}>
            <DefaultTextField
              label="Complemento (Opcional)"
              defaultValue={selectedUser?.complemento}
              name={FORM_FIELDS.COMPLEMENT}
              errorMessage={errors[FORM_FIELDS.COMPLEMENT]}
              onFocus={() => clearError(FORM_FIELDS.COMPLEMENT)}
            />
          </Grid>
        </Grid>
        <Grid item container spacing={2}>
          <Grid item xs={6}>
            <DefaultTextField
              label="Estado"
              placeholder="Ex: MG"
              defaultValue={selectedUser?.estado}
              name={FORM_FIELDS.STATE}
              errorMessage={errors[FORM_FIELDS.STATE]}
              onFocus={() => clearError(FORM_FIELDS.STATE)}
            />
          </Grid>
          <Grid item xs={6}>
            <DefaultTextField
              label="Cidade"
              defaultValue={selectedUser?.cidade}
              name={FORM_FIELDS.CITY}
              errorMessage={errors[FORM_FIELDS.CITY]}
              onFocus={() => clearError(FORM_FIELDS.CITY)}
            />
          </Grid>
        </Grid>
        <Grid item container justify="flex-end" spacing={2}>
          <Grid item>
            <Button
              type="reset"
              variant="outlined"
              color="primary"
              onClick={handleClearForm}
              disabled={loading}
            >
              Limpar Formulário
            </Button>
          </Grid>
          <Grid item>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={debouncedLoading}
            >
              {loading ? 'Criando...' : 'Criar assinatura'}
            </Button>
          </Grid>
        </Grid>
        <Dialog open={generatedBoleto} onClose={() => setGeneratedBoleto(undefined)} maxWidth="md" fullWidth>
          <DialogTitle>Assinatura criada com sucesso</DialogTitle>
          <DialogContent>
            {generatedBoleto?.pdf ? (
              <>
                <DialogContentText>
                  Boleto primeira fatura: <a href={generatedBoleto?.pdf} target="_blank">{generatedBoleto?.pdf}</a>
                </DialogContentText>
                <DialogContentText>
                  {`Vencimento: ${generatedBoleto?.due_at}`}
                </DialogContentText>
                <DialogContentText>
                  O email com o boleto será enviado automaticamente para o aluno
                </DialogContentText>
              </>
            ) : (
              <DialogContentText>
                O aluno já está com o acesso liberado. Como o vencimento
                escolhido é de mais de um mês, a assinatura começa em{' '}
                <strong>
                  {generatedBoleto?.scheduledFor
                    ? format(new Date(generatedBoleto.scheduledFor), 'dd/MM/yyyy')
                    : 'uma data futura'}
                </strong>
                : é nesse dia que o primeiro boleto é gerado e enviado por
                e-mail para ele. Por isso não há boleto para baixar agora.
              </DialogContentText>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="primary"
              type="button"
              onClick={() => setGeneratedBoleto(undefined)}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
      <ModalCreatePlan open={createPlanModalOpen} handleClose={toggleCreatePlanModalOpen} />
    </Container>
  );
}